<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Policy from 'dashboard/components/policy.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  question: {
    type: String,
    required: true,
  },
  answer: {
    type: String,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: 'approved',
  },
  documentable: {
    type: Object,
    default: null,
  },
  assistant: {
    type: Object,
    default: () => ({}),
  },
  updatedAt: {
    type: Number,
    required: true,
  },
  createdAt: {
    type: Number,
    required: true,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  showMenu: {
    type: Boolean,
    default: true,
  },
  showActions: {
    type: Boolean,
    default: false,
  },
  embedding: {
    type: [Array, null],
    default: null,
  },
  isRetrying: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['action', 'navigate', 'select', 'hover']);

const hasEmbedding = computed(() => {
  return Boolean(props.embedding && props.embedding.length > 0);
});

// Check if embedding generation failed (no embedding after 5 minutes)
const embeddingFailed = computed(() => {
  if (hasEmbedding.value) return false;
  
  const now = Date.now() / 1000; // Current time in seconds
  const timeSinceCreation = now - props.createdAt;
  const FIVE_MINUTES = 5 * 60; // 5 minutes in seconds
  
  return timeSinceCreation > FIVE_MINUTES;
});

const embeddingStatus = computed(() => {
  if (hasEmbedding.value) return null;
  
  // Show loading spinner if retrying
  if (props.isRetrying) {
    return {
      tooltip: t('CAPTAIN.RESPONSES.EMBEDDING_RETRYING'),
      icon: 'refresh-ccw',
      color: '#2563eb', // blue-600
      spinning: true,
    };
  }
  
  const now = Date.now() / 1000; // Current time in seconds
  const timeSinceCreation = now - props.createdAt;
  const FIVE_MINUTES = 5 * 60; // 5 minutes in seconds
  const failed = timeSinceCreation > FIVE_MINUTES;
  
  return {
    tooltip: failed
      ? t('CAPTAIN.RESPONSES.EMBEDDING_FAILED')
      : t('CAPTAIN.RESPONSES.EMBEDDING_PENDING'),
    icon: failed ? 'alert-circle' : 'clock',
    color: failed ? '#dc2626' : '#ca8a04', // red-600 : yellow-600
    spinning: false,
  };
});

const { t } = useI18n();

const [showActionsDropdown, toggleDropdown] = useToggle();

const modelValue = computed({
  get: () => props.isSelected,
  set: () => emit('select', props.id),
});

const statusAction = computed(() => {
  if (props.status === 'pending') {
    return [
      {
        label: t('CAPTAIN.RESPONSES.OPTIONS.APPROVE'),
        value: 'approve',
        action: 'approve',
        icon: 'i-lucide-circle-check-big',
      },
    ];
  }
  return [];
});

const menuItems = computed(() => {
  const items = [...statusAction.value];
  
  // Add retry option if embedding failed
  if (embeddingFailed.value) {
    items.push({
      label: t('CAPTAIN.RESPONSES.OPTIONS.RETRY_EMBEDDING'),
      value: 'retry_embedding',
      action: 'retry_embedding',
      icon: 'i-lucide-refresh-cw',
    });
  }
  
  items.push(
    {
      label: t('CAPTAIN.RESPONSES.OPTIONS.EDIT_RESPONSE'),
      value: 'edit',
      action: 'edit',
      icon: 'i-lucide-pencil-line',
    },
    {
      label: t('CAPTAIN.RESPONSES.OPTIONS.DELETE_RESPONSE'),
      value: 'delete',
      action: 'delete',
      icon: 'i-lucide-trash',
    }
  );
  
  return items;
});

const timestamp = computed(() =>
  dynamicTime(props.updatedAt || props.createdAt)
);

const handleAssistantAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};

const handleRetryEmbedding = () => {
  emit('action', {
    action: 'retry_embedding',
    value: 'retry_embedding',
    id: props.id,
  });
};

const handleDocumentableClick = () => {
  emit('navigate', {
    id: props.documentable.id,
    type: props.documentable.type,
  });
};
</script>

<template>
  <CardLayout
    selectable
    class="relative"
    :class="{ 'rounded-md': compact }"
    @mouseenter="emit('hover', true)"
    @mouseleave="emit('hover', false)"
  >
    <div v-show="selectable" class="absolute top-7 ltr:left-3 rtl:right-3">
      <Checkbox v-model="modelValue" />
    </div>
    <div class="flex relative justify-between w-full gap-1">
      <span class="text-base text-n-slate-12 line-clamp-1 flex-1">
        {{ question }}
      </span>
      <div class="flex items-center gap-2">
        <button
          v-if="embeddingStatus && embeddingFailed && !isRetrying"
          v-tooltip="{
            content: t('CAPTAIN.RESPONSES.EMBEDDING_FAILED_RETRY'),
            placement: 'top',
            popperClass: 'captain-status-tooltip',
            container: false,
          }"
          type="button"
          class="flex items-center justify-center rounded hover:bg-n-alpha-2 p-0.5"
          @click="handleRetryEmbedding"
        >
          <Icon
            :icon="`i-lucide-${embeddingStatus.icon}`"
            class="size-4"
            :style="{ color: embeddingStatus.color }"
          />
        </button>
        <Icon
          v-else-if="embeddingStatus"
          v-tooltip="{
            content: embeddingStatus.tooltip,
            placement: 'top',
            popperClass: 'captain-status-tooltip',
            container: false,
          }"
          :icon="`i-lucide-${embeddingStatus.icon}`"
          class="size-4"
          :class="{ 'animate-spin': embeddingStatus.spinning }"
          :style="{ color: embeddingStatus.color }"
        />
        <div v-if="!compact && showMenu" class="flex items-center gap-2">
          <Policy
            v-on-clickaway="() => toggleDropdown(false)"
            :permissions="['administrator']"
            class="relative flex items-center group"
          >
            <Button
              icon="i-lucide-ellipsis-vertical"
              color="slate"
              size="xs"
              class="rounded-md group-hover:bg-n-alpha-2"
              @click="toggleDropdown()"
            />
            <DropdownMenu
              v-if="showActionsDropdown"
              :menu-items="menuItems"
              class="mt-1 ltr:right-0 rtl:right-0 top-full"
              @action="handleAssistantAction($event)"
            />
          </Policy>
        </div>
      </div>
    </div>
    <span class="text-n-slate-11 text-sm line-clamp-5">
      {{ answer }}
    </span>
    <div
      v-if="!compact"
      class="flex items-start justify-between flex-col-reverse md:flex-row gap-3"
    >
      <Policy v-if="showActions" :permissions="['administrator']">
        <div class="flex items-center gap-2 sm:gap-5 w-full">
          <Button
            v-if="status === 'pending'"
            :label="$t('CAPTAIN.RESPONSES.OPTIONS.APPROVE')"
            icon="i-lucide-circle-check-big"
            sm
            link
            class="hover:!no-underline"
            @click="
              handleAssistantAction({ action: 'approve', value: 'approve' })
            "
          />
          <Button
            :label="$t('CAPTAIN.RESPONSES.OPTIONS.EDIT_RESPONSE')"
            icon="i-lucide-pencil-line"
            sm
            slate
            link
            class="hover:!no-underline"
            @click="
              handleAssistantAction({
                action: 'edit',
                value: 'edit',
              })
            "
          />
          <Button
            :label="$t('CAPTAIN.RESPONSES.OPTIONS.DELETE_RESPONSE')"
            icon="i-lucide-trash"
            sm
            ruby
            link
            class="hover:!no-underline"
            @click="
              handleAssistantAction({ action: 'delete', value: 'delete' })
            "
          />
        </div>
      </Policy>
      <div
        class="flex items-center gap-3"
        :class="{ 'justify-between w-full': !showActions }"
      >
        <div class="inline-flex items-center gap-3 min-w-0">
          <span
            v-if="status === 'approved'"
            class="text-sm shrink-0 truncate text-n-slate-11 inline-flex items-center gap-1"
          >
            <Icon icon="i-woot-captain" class="size-3.5" />
            {{ assistant?.name || '' }}
          </span>
          <div
            v-if="documentable"
            class="text-sm text-n-slate-11 grid grid-cols-[auto_1fr] items-center gap-1 min-w-0"
          >
            <Icon
              v-if="documentable.type === 'Captain::Document'"
              icon="i-ph-files-light"
              class="size-3.5"
            />
            <Icon
              v-else-if="documentable.type === 'User'"
              icon="i-ph-user-circle-plus"
              class="size-3.5"
            />
            <Icon
              v-else-if="documentable.type === 'Conversation'"
              icon="i-ph-chat-circle-dots"
              class="size-3.5"
            />
            <span
              v-if="documentable.type === 'Captain::Document'"
              class="truncate"
              :title="documentable.name"
            >
              {{ documentable.name }}
            </span>
            <span
              v-else-if="documentable.type === 'User'"
              class="truncate"
              :title="documentable.available_name"
            >
              {{ documentable.available_name }}
            </span>
            <span
              v-else-if="documentable.type === 'Conversation'"
              class="hover:underline truncate cursor-pointer"
              role="button"
              @click="handleDocumentableClick"
            >
              {{
                t(`CAPTAIN.RESPONSES.DOCUMENTABLE.CONVERSATION`, {
                  id: documentable.display_id,
                })
              }}
            </span>
          </div>
        </div>
        <div
          class="shrink-0 text-sm text-n-slate-11 line-clamp-1 inline-flex items-center gap-1"
        >
          <Icon icon="i-ph-calendar-dot" class="size-3.5" />
          {{ timestamp }}
        </div>
      </div>
    </div>
  </CardLayout>
</template>

<style lang="scss">
// The status tooltip lives inside the Related Responses dialog. Floating-vue
// teleports the popper to body, so the override is intentionally not scoped.
// Raise the z-index above the dialog and allow the long status text to wrap.
.v-popper__popper.captain-status-tooltip {
  z-index: 100000;

  .v-popper__inner {
    max-width: 18rem;
    white-space: normal;
    word-break: break-word;
    line-height: 1.35;
  }
}
</style>
